import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import ReactFlow, {
  Background,
  Elements,
  ReactFlowProps,
  ReactFlowProvider,
} from 'react-flow-renderer';
import { useSelector } from 'react-redux';
import css from './DialogueEditor.module.css';
import { EdgeTypes, edgeTypes } from './EdgeTypes';
import { NodeTypes, nodeTypes } from './NodeTypes';
import { useFetchDialogue } from './use-fetch-dialogue';
import { useGraph } from './use-graph';
import { useLayout } from './use-layout';
import {
  useTranslateExtent,
  DEFAULT_BOTTOM_Y_COMPENSATION,
} from './use-translate-extent';
import { useZoom } from './use-zoom';
import { useDiagram } from './use-diagram';
import WidgetModel from '@mm/state/model/WidgetModel';

interface IDiagramElement {
  id: string;
  widget?: WidgetModel;
}

export interface IDiagramInstance {
  getElements: () => { data: IDiagramElement }[];
}

export type IDiagramElements = Elements<IDiagramElement>;

interface IDialogueEditor
  extends Pick<
    ReactFlowProps,
    'onClick' | 'onPaneClick' | 'onMoveStart' | 'onLoad' | 'paneMoveable'
  > {
  onMount?: Function;
  className?: string;
  campaignId: number;
  dialogueId: number;
  pageGroupId?: number;
  schedulerDateTimeVisible?: boolean;
  scheduledAt?: string;
  intervalInMin?: number;
  container?: React.HTMLAttributes<HTMLDivElement>;
  onChange?: (instance: IDiagramInstance, elements: IDiagramElements) => void;
  withBackground?: boolean;
  bottomYCompensation?: number;
  styles?: {
    container?: string;
  };
}

const TYPES_TO_MAP_PAGE_GROUP_ID = [
  NodeTypes.CONDITION,
  NodeTypes.ADDER,
  NodeTypes.UNICORN_DETECTOR,
  EdgeTypes.EDGEBUTTON,
];

const Editor = ({
  container,
  className,
  dialogueId,
  pageGroupId,
  campaignId,
  schedulerDateTimeVisible = true,
  intervalInMin,
  scheduledAt,
  onClick,
  bottomYCompensation = DEFAULT_BOTTOM_Y_COMPENSATION,
  onPaneClick,
  onMoveStart,
  onMount,
  onChange = () => {},
  withBackground = true,
  paneMoveable = true,
  styles,
}: IDialogueEditor) => {
  const widgets = useSelector((state: StoreState) => state.widget.entities);

  const layout = useLayout();
  const zoom = useZoom();

  const fetch = useFetchDialogue();
  const graph = useGraph();
  const diagram = useDiagram();

  const [instance, setInstance] = useState();

  const translateExtent = useTranslateExtent({ bottomYCompensation });

  const [elements, setElements] = useState<Elements>([]);
  const [positioned, setPositioned] = useState<Elements>([]);

  const [height, setHeight] = useState();

  const setDynamicHeight = useCallback(instance => {
    if (instance) {
      const max = instance.getElements?.().reduce((h, v, i) => {
        return v.position?.y > h ? v.position.y : h;
      }, 0);
      setHeight(max + bottomYCompensation);
    }
  }, []);

  const onReady = useCallback(
    instance => {
      diagram.setState('ready');
      setInstance(instance);

      zoom.onLoad(instance);
      setDynamicHeight(instance);
    },
    [setDynamicHeight, zoom.onLoad],
  );

  useEffect(() => {
    fetch.init(dialogueId, pageGroupId);
  }, []);

  useEffect(() => {
    if (fetch.state === 'ready') {
      fetch.run(dialogueId, pageGroupId);
    }
  }, [campaignId]);

  useEffect(() => {
    if (instance && elements) {
      onChange(instance, elements);
    }
  }, [instance, elements]);

  useEffect(() => {
    if (fetch.state === 'ready') {
      const elements = graph.init(dialogueId, {
        campaignId,
        schedulerDateTimeVisible,
        intervalInMin,
        scheduledAt,
      });

      // TODO as input triggers are merged and actions list rendered internally
      // pass via prop
      elements.forEach(el => {
        if (TYPES_TO_MAP_PAGE_GROUP_ID.includes(el.type)) {
          el.data.pageGroupId = pageGroupId;
        }
      });

      setElements(elements as any as Elements);
    }
  }, [fetch.state, widgets.length]);

  useEffect(() => {
    (async () => {
      try {
        const res = await layout(elements);
        setPositioned(res as any as Elements);
      } catch (err) {
        console.error(err);
      }
    })();
  }, [elements]);

  useEffect(() => {
    setDynamicHeight(instance);
  }, [positioned.length]);

  useEffect(() => {
    if (fetch.state === 'ready') {
      diagram.setState('rendering');
    }
  }, [fetch.state]);

  useEffect(() => {
    onMount?.();
  }, []);

  return (
    <div
      className={classNames(className, css.container, styles?.container)}
      style={{ height }}
      {...container}
    >
      {fetch.state === 'ready' && !!positioned.length && (
        <ReactFlow
          key="edges"
          onLoad={onReady}
          elements={positioned}
          edgeTypes={edgeTypes}
          nodeTypes={nodeTypes}
          nodesConnectable={false}
          zoomOnScroll={false}
          zoomOnPinch={false}
          zoomOnDoubleClick={false}
          onClick={onClick}
          onPaneClick={onPaneClick}
          onMoveStart={onMoveStart}
          nodesDraggable={false}
          panOnScroll={false}
          preventScrolling={false}
          translateExtent={translateExtent}
          snapToGrid
          paneMoveable={paneMoveable}
        >
          {withBackground && <Background />}
        </ReactFlow>
      )}
    </div>
  );
};

export const DialogueEditor = (props: IDialogueEditor) => {
  return (
    <ReactFlowProvider>
      <Editor {...props} />
    </ReactFlowProvider>
  );
};
